// ── usePolymarketRelayOrders ────────────────────────────────────
//
// Auto-polling React hook backed by /api/polymarket/orders (the org-key
// trader proxy). Returns a flat state + actions object so a single hook
// invocation is enough for "show me my live orders + let me cancel one."
//
// Polling cadence matches kalshi's accountStore (5s default). When the
// relay returns an unavailable code (NOT_IN_ORG / PERSONAL_KEY_MODE /
// NO_POLY_CRED) the hook stops the interval — there's no point asking
// every 5s when the answer is "you don't have poly trading available."
// `refresh()` still works in that state so the consumer can re-probe
// after the user fixes the underlying cause (e.g., admin adds a cred).
//
// Track 3 scope: open orders only. Fills/positions need a server-side
// poller + schema work — tracked in the defer note at the galactus root.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  cancelPolyOrderViaRelay,
  listPolyOpenOrdersViaRelay,
  placePolyOrderViaRelay,
  type PolyRelayOpenOrder,
  type PolyRelayUnavailableCode,
} from '@/lib/polymarket/relay';
import { describePolyOrder } from '@/lib/polymarket/describeOrder';
import { startVisibilityAwareInterval } from '@/lib/visibilityThrottle';

export type PolyRelayOrdersStatus = 'idle' | 'loading' | 'ready' | 'error' | 'unavailable';

export interface PolyRelayOrdersState {
  orders: PolyRelayOpenOrder[];
  status: PolyRelayOrdersStatus;
  error: string | null;
  unavailableCode: PolyRelayUnavailableCode | null;
  lastFetchMs: number | null;
}

export interface UsePolymarketRelayOrdersOptions {
  /** Off → hook is inert (no fetch, no poll). Defaults to true. */
  enabled?: boolean;
  /** Poll interval in ms. Defaults to 5000. */
  pollMs?: number;
}

/** Hints the caller passes through to `replace()` so the new-place leg
 *  carries the same market-specific config the original order needed.
 *  Optional because the relay has sensible defaults — but a NEG_RISK
 *  market WILL reject a re-place without `negRisk: true`, so consumers
 *  that know the market context should pass these through. */
export interface PolyRelayReplaceOptions {
  tickSize?: string;
  negRisk?: boolean;
  /** Override the new order's size. Omitted → use the original order's
   *  remaining size (effectively price-only edit). Polymarket has no
   *  queue-preserving decrease equivalent to Kalshi's /decrease endpoint,
   *  so any size change always goes through cancel + new-place. */
  nextQuantity?: number;
}

export interface UsePolymarketRelayOrdersReturn extends PolyRelayOrdersState {
  refresh: () => Promise<void>;
  cancel: (orderId: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  /** Cancel + immediately re-place an open Polymarket order at a new price.
   *  Derives tokenId, action, and remaining contracts from the existing
   *  open-order; caller provides `newPriceCents` and optional tickSize /
   *  negRisk hints. Polymarket has no atomic amend — there's a brief window
   *  between the cancel and the new place where the order isn't on the book. */
  replace: (
    orderId: string,
    newPriceCents: number,
    options?: PolyRelayReplaceOptions,
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
}

const DEFAULT_POLL_MS = 5000;

export function usePolymarketRelayOrders(
  options: UsePolymarketRelayOrdersOptions = {},
): UsePolymarketRelayOrdersReturn {
  const enabled = options.enabled ?? true;
  const pollMs = options.pollMs ?? DEFAULT_POLL_MS;

  const [orders, setOrders] = useState<PolyRelayOpenOrder[]>([]);
  const [status, setStatus] = useState<PolyRelayOrdersStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [unavailableCode, setUnavailableCode] = useState<PolyRelayUnavailableCode | null>(null);
  const [lastFetchMs, setLastFetchMs] = useState<number | null>(null);

  // Ref guard so multiple in-flight fetches don't trample each other's
  // state when the user manually refreshes during a tick.
  const inFlightRef = useRef(false);

  const refresh = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setStatus((prev) => (prev === 'idle' ? 'loading' : prev));
    try {
      const out = await listPolyOpenOrdersViaRelay();
      setLastFetchMs(Date.now());
      if (out.ok) {
        setOrders(out.orders);
        setError(null);
        setUnavailableCode(null);
        setStatus('ready');
      } else if (out.code) {
        setOrders([]);
        setError(out.error);
        setUnavailableCode(out.code);
        setStatus('unavailable');
      } else {
        setError(out.error);
        setUnavailableCode(null);
        setStatus('error');
      }
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  const cancel = useCallback(
    async (orderId: string) => {
      const out = await cancelPolyOrderViaRelay(orderId);
      if (out.ok) {
        // Optimistic local removal — the next poll will reconcile if
        // CLOB hadn't yet propagated the cancel.
        setOrders((prev) =>
          prev.filter((o) => {
            const id = o.id ?? o.orderID ?? o.orderId;
            return id !== orderId;
          }),
        );
        // Kick off a refresh to pick up the authoritative state.
        void refresh();
        return { ok: true as const };
      }
      return { ok: false as const, error: out.error };
    },
    [refresh],
  );

  // Replace = cancel + new-place. Polymarket has no atomic amend (same as
  // Kalshi). If the place fails after cancel succeeded, the caller is told
  // explicitly so the trader knows the original is gone.
  const replace = useCallback(
    async (
      orderId: string,
      newPriceCents: number,
      options: PolyRelayReplaceOptions = {},
    ): Promise<{ ok: true } | { ok: false; error: string }> => {
      if (!Number.isFinite(newPriceCents) || newPriceCents < 1 || newPriceCents > 99) {
        return { ok: false, error: 'Replacement price must be between 1¢ and 99¢.' };
      }
      const existing = orders.find(
        (o) => (o.id ?? o.orderID ?? o.orderId) === orderId,
      );
      if (!existing) {
        return { ok: false, error: 'Order to replace was not found in the open-orders list.' };
      }
      const tokenId = typeof existing.asset_id === 'string' ? existing.asset_id : null;
      if (!tokenId) {
        return { ok: false, error: 'Order has no asset_id — cannot replace.' };
      }
      // SDK returns "BUY" / "SELL" strings; lowercase normalize. Default to
      // 'buy' if the field is missing — open orders we'd actually want to
      // replace are virtually always buys in this app's market-making flow.
      const sideStr = String(existing.side ?? '').toLowerCase();
      const action: 'buy' | 'sell' = sideStr === 'sell' ? 'sell' : 'buy';
      // Remaining size: prefer SDK's `size` (open remaining); fall back to
      // original_size - size_matched. CLOB allows fractional sizes, so we
      // preserve floats rather than rounding.
      const toNumber = (v: unknown): number => {
        if (typeof v === 'number') return v;
        if (typeof v === 'string') return Number.parseFloat(v);
        return NaN;
      };
      let contracts = toNumber(existing.size);
      if (!Number.isFinite(contracts) || contracts <= 0) {
        const orig = toNumber(existing.original_size);
        const matched = toNumber(existing.size_matched);
        if (Number.isFinite(orig) && Number.isFinite(matched)) {
          contracts = orig - matched;
        }
      }
      if (!Number.isFinite(contracts) || contracts <= 0) {
        return { ok: false, error: 'Order remaining size is missing or invalid.' };
      }
      // If the caller supplied an explicit nextQuantity, override the
      // derived remaining size with it. Validation here mirrors the Kalshi
      // accountStore.replaceOrder shape — integer ≥ 1 — even though Poly
      // CLOB technically accepts fractional sizes. The modal upstream
      // enforces integer-only entry for consistency with Kalshi's UX, so
      // a sub-integer nextQuantity here is a caller bug rather than user
      // input. The relay will surface CLOB's own minimum-size rejection
      // ("Size (X) lower than the minimum: 5") if applicable.
      if (options.nextQuantity !== undefined) {
        const requested = options.nextQuantity;
        if (
          !Number.isFinite(requested) ||
          !Number.isInteger(requested) ||
          requested < 1
        ) {
          return {
            ok: false,
            error: 'Replacement quantity must be a positive integer ≥ 1.',
          };
        }
        contracts = requested;
      }

      const meta = describePolyOrder(tokenId);
      const existingTickSize =
        typeof existing.tickSize === 'string' ? existing.tickSize : undefined;
      const existingNegRisk =
        typeof existing.negRisk === 'boolean' ? existing.negRisk : undefined;
      const tickSize = options.tickSize ?? meta?.tickSize ?? existingTickSize;
      const negRisk = options.negRisk ?? meta?.negRisk ?? existingNegRisk;

      // 1) Cancel old. Bail before touching place if cancel rejects.
      const cancelOut = await cancelPolyOrderViaRelay(orderId);
      if (!cancelOut.ok) {
        return { ok: false, error: `Cancel failed: ${cancelOut.error}` };
      }
      // Optimistic local removal so the UI doesn't render a doomed order
      // during the brief uncovered window.
      setOrders((prev) =>
        prev.filter((o) => (o.id ?? o.orderID ?? o.orderId) !== orderId),
      );

      // 2) Place new at the new price. post_only=true keeps an edit from
      // accidentally crossing the spread.
      const placeOut = await placePolyOrderViaRelay({
        tokenId,
        action,
        priceCents: newPriceCents,
        contracts,
        postOnly: true,
        tickSize,
        negRisk,
      });
      // Always refresh so the open-orders list reconciles regardless of
      // whether the place succeeded.
      void refresh();
      if (!placeOut.ok) {
        return {
          ok: false,
          error: `Original order was cancelled, but the replacement failed to place: ${placeOut.error}`,
        };
      }
      return { ok: true as const };
    },
    [orders, refresh],
  );

  // Initial fetch + polling lifecycle. Polling pauses while the relay
  // says "unavailable" — manual refresh still works.
  useEffect(() => {
    if (!enabled) return;

    let stopped = false;
    void refresh();

    const tick = () => {
      // Inline-read latest status by closing over the setter pattern —
      // we don't want to re-create the interval on every status change.
      setStatus((current) => {
        if (!stopped && current !== 'unavailable') {
          void refresh();
        }
        return current;
      });
    };

    // 1/6 poll rate while the tab is hidden, immediate tick on refocus.
    const stopInterval = startVisibilityAwareInterval(tick, pollMs);
    return () => {
      stopped = true;
      stopInterval();
    };
  }, [enabled, pollMs, refresh]);

  return {
    orders,
    status,
    error,
    unavailableCode,
    lastFetchMs,
    refresh,
    cancel,
    replace,
  };
}
